import * as React from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import AssignmentIcon from "@material-ui/icons/Assignment";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import StorefrontIcon from "@material-ui/icons/Storefront";
import LockIcon from "@material-ui/icons/Lock";
import HistoryIcon from "@material-ui/icons/History";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import RoomIcon from "@material-ui/icons/Room";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxOpen } from "@fortawesome/free-solid-svg-icons/faBoxOpen";
import { Appointment } from "../../generated/nest-graphql";
import { appointmentWindow, formatWindow, ScheduleTag, tagsFor, vehicleLabelOf } from "./scheduleData.utils";
import { useHasPartsTab } from "../../hooks/useUiv";
import { JobStatusPill } from "../Jobs/JobStatusPill";
import { ChipSize } from "../ui";

// Inline, not Tailwind: MUI v4 injects .MuiChip-root after the Tailwind sheet, so a
// bg-* class on a Chip loses at equal specificity and renders MUI's default grey.
const chipStyle: React.CSSProperties = { height: 22, fontSize: 12, fontWeight: 600 };

/** One icon per tag, so the row reads at a glance rather than needing to be read. */
const tagIcons: Record<ScheduleTag, typeof StorefrontIcon> = {
  Dealer: StorefrontIcon,
  Fixed: LockIcon,
  Rescheduled: HistoryIcon,
};

const tagStyle: React.CSSProperties = {
  ...chipStyle,
  height: 22,
  fontSize: 11,
  backgroundColor: "#16202e",
  color: "#cbd5e1",
  border: "1px solid #44576e",
};

/**
 * One job on the schedule list. Shares the part order card's shell on purpose — the
 * two tabs are read in the same session and should feel like one app.
 */
export const ScheduleJobCard: React.FC<{ appointment: Appointment }> = ({ appointment }) => {
  const history = useHistory();
  const hasPartsTab = useHasPartsTab();
  const job = appointment?.job;
  const jobId = job?.id;
  const { start, end } = appointmentWindow(appointment);
  const windowLabel = formatWindow(start, end);
  const vehicle = vehicleLabelOf(job);
  const address = job?.serviceLocation;
  const tags = tagsFor(appointment);

  return (
    <div className="border border-[#55697f] rounded-md bg-[#2b3a4f] overflow-hidden">
      <div className="p-3 flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          {/* The whole identity block navigates, matching the part order card. */}
          <button
            type="button"
            onClick={() => jobId && history.push(`/jobs/${jobId}`)}
            disabled={!jobId}
            aria-label={jobId ? `Open job ${job?.jobNumber ?? ""}` : undefined}
            className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 m-0 cursor-pointer disabled:cursor-default"
          >
            <span className="flex items-center gap-2 min-w-0">
              <AssignmentIcon style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
              <Typography variant="h6" style={{ fontSize: 17, fontWeight: 600 }}>
                Job# <span style={{ textDecoration: jobId ? "underline" : "none" }}>{job?.jobNumber ?? "—"}</span>
              </Typography>
            </span>
            {vehicle && (
              <span className="flex items-center gap-1" style={{ color: "#cbd5e1" }}>
                <DirectionsCarIcon style={{ fontSize: 15, flexShrink: 0 }} />
                <Typography variant="body2" style={{ color: "#cbd5e1" }}>
                  {vehicle}
                </Typography>
              </span>
            )}
          </button>
          <JobStatusPill status={job?.status} size={ChipSize.SMALL} style={chipStyle} />
        </div>

        {windowLabel && (
          <span className="flex items-center gap-1 text-gray-400">
            <AccessTimeIcon style={{ fontSize: 15, flexShrink: 0 }} />
            <Typography variant="body2" className="text-gray-400">
              {windowLabel}
            </Typography>
          </span>
        )}

        {address && (
          <span className="flex items-start gap-1 text-gray-400">
            <RoomIcon style={{ fontSize: 15, flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2" className="text-gray-400">
              {address}
            </Typography>
          </span>
        )}

        {tags.length > 0 && (
          <span className="flex flex-wrap items-center gap-1 mt-1">
            {tags.map((tag) => {
              const TagIcon = tagIcons[tag];
              return (
                <Chip
                  key={tag}
                  size="small"
                  style={tagStyle}
                  icon={<TagIcon style={{ fontSize: 13, color: "#cbd5e1" }} />}
                  label={tag}
                />
              );
            })}
          </span>
        )}
      </div>

      {/*
        Gated on the same variant as the tab itself. Without it the page redirects
        home, so an ungated button would bounce the technician straight back here.
      */}
      {hasPartsTab && (
        <div className="flex gap-2 px-3 pb-3">
          <Button
            size="small"
            variant="contained"
            disableElevation
            fullWidth
            // The Parts tab's own icon, so the button looks like where it goes.
            startIcon={<FontAwesomeIcon icon={faBoxOpen as any} style={{ fontSize: "0.9rem" }} />}
            disabled={!jobId}
            // Lands on the parts tab already grouped by job, with this job in view.
            onClick={() => jobId && history.push(`/parts?groupBy=job&job=${jobId}`)}
            style={{ textTransform: "none", fontWeight: 600, backgroundColor: "#bfdbfe", color: "#1e3a8a" }}
          >
            View Parts
          </Button>
        </div>
      )}
    </div>
  );
};
